import { useCallback, useState, useRef } from "react";

import { createMeter } from "../components/salesDocuments-v2/salesDocuments.constants";
import { CustomerDetailsProps, CustomerInfoProps } from "../pages/SalesDocuments-v2";
import {
  ElectricityData,
  GasData,
  SalesDocumentPayload,
} from "../services/salesDocuments-v2.service";
import { Meter, FuelType, DocType } from "../types/salesDocuments.types";

export const useMeterManager = (
  customerZohoId: string,
  companyName: string,
  customerDetails: CustomerDetailsProps,
  customerInfo: CustomerInfoProps,
) => {
  const [meters, setMeters] = useState<Meter[]>([]);
  const [activeMeter, setActiveMeter] = useState(1);
  const [nextMeterId, setNextMeterId] = useState(1);
  const [validatedMeters, setValidatedMeters] = useState<Set<number>>(new Set());
  const [isGenerating, setIsGenerating] = useState(false);
  const [failedMeters, setFailedMeters] = useState<number[]>([]);

  const meterPayloadGetters = useRef(new Map<number, () => ElectricityData | GasData | null>());
  const meterValidityCheckers = useRef(new Map<number, () => boolean>());

  // Stable identities so the meters' registration effects only run on mount
  const registerPayloadGetter = useCallback(
    (id: number, getter: () => ElectricityData | GasData | null) => {
      meterPayloadGetters.current.set(id, getter);
    },
    [],
  );

  const registerValidityChecker = useCallback((id: number, checker: () => boolean) => {
    meterValidityCheckers.current.set(id, checker);
  }, []);

  const runValidityChecker = (id: number): boolean => {
    return meterValidityCheckers.current.get(id)?.() ?? true;
  };

  const handleAddMeter = (fuelType: FuelType) => {
    const newId = nextMeterId;
    setMeters((prev) => [...prev, createMeter(newId, fuelType)]);
    setActiveMeter(newId);
    setNextMeterId(nextMeterId + 1);
  };

  const handleDeleteMeter = (meterId: number) => {
    setMeters((prev) => {
      const remaining = prev.filter((m) => m.id !== meterId);
      if (activeMeter === meterId) {
        setActiveMeter(remaining.length ? remaining[remaining.length - 1].id : 1);
      }
      return remaining;
    });

    // Cleanup
    meterPayloadGetters.current.delete(meterId);
    meterValidityCheckers.current.delete(meterId);
  };

  const buildPayload = (docType: DocType): SalesDocumentPayload => {
    const electricity_data: ElectricityData[] = [];
    const gas_data: GasData[] = [];

    meters.forEach((meter) => {
      const data = meterPayloadGetters.current.get(meter.id)?.();
      if (!data) return;
      if (meter.fuelType === "electricity") electricity_data.push(data as ElectricityData);
      else gas_data.push(data as GasData);
    });

    return {
      format: docType,
      zoho_id: customerZohoId,
      company_name: companyName,
      customer_details: {
        registration_number: customerDetails.registrationNumber,
        type_of_business: customerDetails.typeOfBusiness,
        business_address: customerDetails.businessAddress,
      },
      customer_info: {
        contact_name: customerInfo.contactName,
        phone_number: customerInfo.phoneNumber,
        email_address: customerInfo.emailAddress,
        ...(customerInfo.dateOfBirth && { date_of_birth: customerInfo.dateOfBirth }),
      },
      quote: { electricity_data, gas_data },
    };
  };

  return {
    meters,
    setMeters,
    activeMeter,
    setActiveMeter,
    isGenerating,
    setIsGenerating,
    failedMeters,
    setFailedMeters,
    validatedMeters,
    runValidityChecker,
    setValidatedMeters,
    handleAddMeter,
    handleDeleteMeter,
    registerPayloadGetter,
    registerValidityChecker,
    buildPayload,
  };
};
